import { buildPortalSteps, type InterventionStatus, type PortalStep, type PortalStepKey } from '@garage/shared';
import type { ComponentType } from 'react';
import { cx } from '../../ui/cx.js';
import { CarIcon, CheckCircleIcon, ClipboardIcon, FlagIcon, KeyIcon, PackageIcon, SearchIcon, WrenchIcon } from '../../ui/icons.js';

/**
 * Frise d'étapes à icônes (vague 7), **la même** dans la fiche dossier et sur le portail client : les étapes
 * viennent du contrat (`PortalView.steps`, `buildPortalSteps`), avec leur libellé et leur clé d'icône ;
 * l'interface traduit seulement la clé en pictogramme. Composant autonome : le portail par onglets (après la
 * vague 7) le réutilise tel quel. Même dessin que `Stepper` (`src/ui`), qui ne connaît pas encore les clés du
 * contrat (demande de changement L5).
 */
type Icon = ComponentType<{ size?: number; className?: string; strokeWidth?: number }>;

const ICONS: Record<string, Icon> = {
  car: CarIcon,
  search: SearchIcon,
  'check-circle': CheckCircleIcon,
  package: PackageIcon,
  wrench: WrenchIcon,
  key: KeyIcon,
  flag: FlagIcon,
};

type Tone = 'brand' | 'attention' | 'success';
/** Couleur de l'étape en cours : ambre si le client ou une pièce est attendu, vert quand c'est prêt. */
const TONE_OF: Partial<Record<PortalStepKey, Tone>> = { decision: 'attention', waiting_parts: 'attention', ready: 'success' };
const CURRENT_TONE: Record<Tone, string> = {
  brand: 'bg-brand text-on-brand',
  attention: 'border-[1.5px] border-warning bg-warning-bg text-warning-fg',
  success: 'border-[1.5px] border-success bg-success-bg text-success-fg',
};
const SR_STATE: Record<PortalStep['state'], string> = { done: 'terminée', current: 'étape actuelle', todo: 'à venir' };

/** Étapes du contrat ; à défaut (API d'avant la vague 7), calculées avec la même fonction du contrat. */
export function stepsOf(steps: PortalStep[] | undefined, status: InterventionStatus, hasSentComplement: boolean): PortalStep[] {
  return steps && steps.length > 0 ? steps : buildPortalSteps({ status, hasSentComplement });
}

export function ProgressSteps({
  steps,
  label = 'Étapes',
  size = 'md',
  hints = {},
  className,
}: {
  steps: PortalStep[];
  label?: string;
  /** `sm` : fiche dossier (cercles de 28 px) ; `md` : portail client (32 px). */
  size?: 'sm' | 'md';
  /** Précision de l'étape en cours, lue par les lecteurs d'écran (ex. « votre accord est attendu »). */
  hints?: Partial<Record<PortalStepKey, string>>;
  className?: string;
}) {
  const circle = size === 'sm' ? 'size-7' : 'size-8';
  const icon = size === 'sm' ? 15 : 16;
  const lineTop = size === 'sm' ? 'top-3.5' : 'top-4';
  return (
    <ol aria-label={label} className={cx('grid', className)} style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
      {steps.map((s, i) => {
        const Glyph = ICONS[s.icon] ?? ClipboardIcon;
        const reached = s.state !== 'todo';
        const hint = s.state === 'current' ? hints[s.key] : undefined;
        return (
          <li key={s.key} data-step={s.key} className="relative flex min-w-0 flex-col items-center text-center" aria-current={s.state === 'current' ? 'step' : undefined}>
            {i > 0 ? <span aria-hidden className={cx('absolute right-1/2 left-[-50%] h-px', lineTop, reached ? 'bg-brand-strong' : 'bg-line-strong')} /> : null}
            <span
              aria-hidden
              className={cx(
                'relative z-10 grid shrink-0 place-items-center rounded-full ring-4 ring-surface',
                circle,
                s.state === 'done' && 'bg-brand-soft text-brand-strong',
                s.state === 'current' && CURRENT_TONE[TONE_OF[s.key] ?? 'brand'],
                s.state === 'todo' && 'border border-line-strong bg-surface text-ink-3',
              )}
            >
              <Glyph size={icon} strokeWidth={1.6} />
            </span>
            <span
              className={cx(
                'mt-1.5 block max-w-full px-0.5 text-2xs leading-tight sm:text-xs',
                s.state === 'current' ? 'font-medium text-ink' : s.state === 'done' ? 'text-ink-2' : 'text-ink-3',
              )}
            >
              {s.label}
              <span className="sr-only">
                {' '}
                ({SR_STATE[s.state]}
                {hint ? ` : ${hint}` : ''})
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
